import { useId, useState } from "react"
import { validateEmail } from "../lib/email"
import { validateDestinationUrl } from "../lib/urls"

// The fields of a hosted contact page (#332): what a business card carries,
// nothing more. Shared by the homepage builder, the dashboard creator and the
// two edit surfaces. The server is the gate (ContactPage); this is the form.
//
// Four fields first (the name, the business, the mobile, the page's email),
// the rest behind "More details". The page's email is not the email gate's
// address: a delivery address is private and a page address is public, so it
// is only copied when the person taps "Same as my email".

// ContactPage::LIMITS
export const CONTACT_PAGE_LIMITS = {
  first_name: 50, last_name: 50, business_name: 80, job_title: 60,
  mobile_phone: 30, work_phone: 30, email: 254, website_url: 2048,
  street: 100, city: 60, region: 60, postal_code: 20, country: 60,
}

export const CONTACT_PAGE_FIELDS = Object.keys(CONTACT_PAGE_LIMITS)

export const EMPTY_CONTACT_PAGE = Object.fromEntries(CONTACT_PAGE_FIELDS.map((f) => [ f, "" ]))

const MORE_FIELDS = [ "job_title", "work_phone", "website_url", "street", "city", "region", "postal_code", "country" ]

// Every field trimmed, an empty one left out. What the create and the edit send.
export function cleanContactPage(page) {
  const out = {}
  for (const field of CONTACT_PAGE_FIELDS) {
    const value = (page?.[field] || "").trim()
    if (value) out[field] = value
  }
  return out
}

// A saved page's fields as form state (nil to "").
export function contactPageFromServer(fields) {
  return Object.fromEntries(CONTACT_PAGE_FIELDS.map((f) => [ f, fields?.[f] || "" ]))
}

export function contactPagesEqual(a, b) {
  return CONTACT_PAGE_FIELDS.every((f) => (a?.[f] || "").trim() === (b?.[f] || "").trim())
}

function phoneError(value) {
  if (!value) return null
  const digits = (value.match(/\d/g) || []).length
  if (!/^\+?[0-9 ().-]+$/.test(value) || digits < 7 || digits > 15) {
    return "Use a phone number with 7 to 15 digits."
  }
  return null
}

// The rules the server applies, for feedback before a round trip. Returns
// { base, fields } or null. The server stays authoritative.
export function validateContactPage(page) {
  const p = cleanContactPage(page)
  const fields = {}
  const base = []
  if (!p.first_name && !p.last_name && !p.business_name) base.push("Add a name or a business name.")
  if (!p.mobile_phone && !p.work_phone && !p.email && !p.website_url) {
    base.push("Add a way to reach you: a phone number, an email or a website.")
  }
  if (phoneError(p.mobile_phone)) fields.mobile_phone = phoneError(p.mobile_phone)
  if (phoneError(p.work_phone)) fields.work_phone = phoneError(p.work_phone)
  if (p.email && validateEmail(p.email)) fields.email = "That doesn't look like a valid email."
  if (p.website_url) {
    const { error } = validateDestinationUrl(p.website_url)
    if (error || /^mailto:/i.test(p.website_url)) fields.website_url = "Use a web address, like yoursite.com."
  }
  return base.length || Object.keys(fields).length ? { base: base.join(" "), fields } : null
}

// The server's field errors for a refused page ({ details: { mobile_phone: [...] } }),
// as { base, fields } for this form.
export function contactPageErrorsFromServer(details, message) {
  if (!details) return message ? { base: message, fields: {} } : null
  const fields = {}
  for (const field of CONTACT_PAGE_FIELDS) {
    const list = details[field] || details[`contact_page.${field}`]
    if (Array.isArray(list) && list[0]) fields[field] = `${LABELS[field]} ${list[0]}`
  }
  const base = (details.base || []).join(" ")
  return { base: base || (Object.keys(fields).length ? "" : message || ""), fields }
}

const LABELS = {
  first_name: "First name", last_name: "Last name", business_name: "Business name", job_title: "Job title",
  mobile_phone: "Mobile phone", work_phone: "Work phone", email: "Email", website_url: "Website",
  street: "Street", city: "City", region: "State or region", postal_code: "Postal code", country: "Country",
}

const PLACEHOLDERS = {
  first_name: "Alex", last_name: "Rivera", business_name: "Rivera Plumbing", job_title: "Owner",
  mobile_phone: "+1 555 123 4567", work_phone: "+1 555 765 4321", email: "alex@riveraplumbing.com",
  website_url: "riveraplumbing.com", street: "12 Main St", city: "Springfield", region: "IL",
  postal_code: "62701", country: "United States",
}

const INPUT_TYPES = {
  mobile_phone: { type: "tel", inputMode: "tel", autoComplete: "off" },
  work_phone: { type: "tel", inputMode: "tel", autoComplete: "off" },
  email: { type: "email", inputMode: "email", autoComplete: "off" },
  website_url: { type: "text", inputMode: "url", autoComplete: "off" },
}

const fieldClasses = (hasError) =>
  "w-full px-4 py-3 rounded border bg-white dark:bg-midnight text-ink dark:text-cloud text-base placeholder:text-[color:var(--color-muted)] dark:placeholder:text-muted-light " +
  "focus:outline-none focus:ring-2 focus:ring-voltage focus:border-voltage disabled:opacity-60 disabled:cursor-not-allowed " +
  (hasError ? "border-[color:var(--color-fail)]" : "border-[color:var(--color-border)] dark:border-storm")

const linkBtn =
  "text-sm font-semibold text-voltage-deep dark:text-voltage-light underline underline-offset-2 hover:no-underline " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"

export default function ContactPageFields({ page, onChange, errors = null, disabled = false, gateEmail = null, compact = false }) {
  const idBase = useId()
  const fieldErrors = errors?.fields || {}
  const moreHasValue = MORE_FIELDS.some((f) => (page[f] || "").trim() || fieldErrors[f])
  const [moreOpen, setMoreOpen] = useState(moreHasValue)
  const showMore = moreOpen || moreHasValue

  const set = (field) => (e) => onChange({ ...page, [field]: e.target.value })

  const input = (field, { className = "" } = {}) => {
    const id = `${idBase}-${field}`
    const error = fieldErrors[field]
    return (
      <div className={className}>
        <label htmlFor={id} className="block text-sm font-semibold mb-1.5 text-coal dark:text-cloud">
          {field === "email" ? "Email on your page" : LABELS[field]}
        </label>
        <input
          id={id}
          name={`contact_page[${field}]`}
          type="text"
          {...(INPUT_TYPES[field] || { autoComplete: "off" })}
          maxLength={CONTACT_PAGE_LIMITS[field]}
          placeholder={PLACEHOLDERS[field]}
          value={page[field] || ""}
          onChange={set(field)}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          data-1p-ignore=""
          data-lpignore="true"
          data-bwignore=""
          data-form-type="other"
          className={fieldClasses(error)}
        />
        {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-[color:var(--color-fail)]">{error}</p>}
      </div>
    )
  }

  const grid = compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 gap-3 sm:grid-cols-2"

  return (
    <div className="flex flex-col gap-3" data-contact-page-fields>
      <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light">
        People who scan your code see these details.
      </p>
      <div className={grid}>
        {input("first_name")}
        {input("last_name")}
        {input("business_name", { className: compact ? "" : "sm:col-span-2" })}
        {input("mobile_phone")}
        <div>
          {input("email")}
          {gateEmail && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange({ ...page, email: gateEmail })}
              className={"mt-1.5 " + linkBtn}
            >
              Same as my email
            </button>
          )}
        </div>
      </div>

      {showMore ? (
        <div className={grid}>
          {input("job_title")}
          {input("work_phone")}
          {input("website_url", { className: compact ? "" : "sm:col-span-2" })}
          {input("street", { className: compact ? "" : "sm:col-span-2" })}
          {input("city")}
          {input("region")}
          {input("postal_code")}
          {input("country")}
        </div>
      ) : (
        <button type="button" onClick={() => setMoreOpen(true)} disabled={disabled} className={"self-start " + linkBtn}>
          More details
        </button>
      )}

      {errors?.base && (
        <p className="text-sm text-[color:var(--color-fail)]" role="alert">{errors.base}</p>
      )}

      <ContactPageSummary page={page} />
    </div>
  )
}

// What people will see, before Generate: the name and the buttons. A summary
// of the page, not a copy of it.
export function ContactPageSummary({ page }) {
  const p = cleanContactPage(page)
  const name = [ p.first_name, p.last_name ].filter(Boolean).join(" ") || p.business_name
  const buttons = [ "Save contact" ]
  if (p.mobile_phone || p.work_phone) buttons.push("Call")
  if (p.mobile_phone) buttons.push("Text")
  if (p.email) buttons.push("Email")
  if (p.website_url) buttons.push("Website")
  if (!name) return null
  return (
    <div className="rounded border border-[color:var(--color-border)] dark:border-storm bg-cloud dark:bg-coal px-4 py-3 text-sm">
      <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">People who scan see</p>
      <p className="mt-0.5 font-semibold text-coal dark:text-cloud break-words">{name}</p>
      <p className="mt-0.5 text-[color:var(--color-muted)] dark:text-muted-light">{buttons.join(" · ")}</p>
    </div>
  )
}
